import PageHeader from "@/components/site/PageHeader";
import { Plus } from "lucide-react";

const FAQS = [
    {
        q: "What if I am currently a W-2 employee?",
        a: [
            "You can still express interest in a 1099er opportunity. Many experienced medical sales professionals work full-time for another company, and we understand the need for discretion.",
            "However, it is your responsibility to understand your current employment agreement, non-compete or non-solicitation obligations, conflict-of-interest policies, hospital credentialing requirements, and any other restrictions that may apply to you.",
            "Submitting your information does not commit you to anything. We can have a confidential conversation first and determine whether an opportunity makes sense.",
        ],
    },
    {
        q: "Where is the product approved?",
        a: [
            "Product availability and account approval vary by manufacturer, geography, health system, and individual facility.",
            "Once we understand where you are located and the accounts you currently service, we can determine where the product is currently available and where an approval process may be required.",
            "Don't let a lack of current approval necessarily stop you from reaching out. In many cases, the right physician relationship is exactly what helps begin that process.",
        ],
    },
    {
        q: "How do you assign territories?",
        a: [
            "We don't think about territories the same way a traditional distributorship does.",
            "1099er is designed around relationships, not ZIP codes.",
            "Rather than giving one representative an entire city or state, we look at the physicians and accounts where you already have established relationships. Multiple representatives may work within the same geographic market as long as their relationships and opportunities don't conflict.",
            "Our goal is to protect legitimate opportunities while putting the product in the hands of the representative with the strongest relationship.",
        ],
    },
    {
        q: "How do I get the product?",
        a: [
            "That depends on the specific product and opportunity.",
            "Once you are approved and contracted, 1099er and the participating company will walk you through product training, ordering, samples or inventory requirements, shipping, case support, and any necessary credentialing.",
            "We want the process to be simple: you identify the opportunity; we help provide the infrastructure to support it.",
        ],
    },
    {
        q: "Who am I contracted through?",
        a: [
            "The contracting structure may vary depending on the featured company and product.",
            "Before you begin selling, you will receive a written agreement clearly outlining who you are contracted with, your commission structure, payment terms, responsibilities, account or opportunity protection, and any other applicable terms.",
            "There should be no ambiguity about how you get paid before you begin working an opportunity.",
        ],
    },
    {
        q: "Do I need to tell you which surgeons I work with?",
        a: [
            "Not initially.",
            "We intentionally keep the first step simple. Tell us your general geography, specialty experience, and the type of physicians you currently work with.",
            "If there appears to be a fit, we'll have a confidential conversation before asking for more specific information.",
            "We aren't interested in collecting physician names simply to build a database. Specific relationships should only be discussed when there is a legitimate opportunity to explore.",
        ],
    },
    {
        q: "Will you contact my surgeons or accounts?",
        a: [
            "Not without your involvement.",
            "The value you bring to 1099er is the relationship. We aren't looking to take that relationship away from you.",
            "If an opportunity progresses, we'll establish how the account and physician relationship will be handled before outreach occurs.",
        ],
    },
    {
        q: "How are commissions paid?",
        a: [
            "Compensation varies by product and participating company. Every opportunity listed through 1099er will clearly explain the applicable commission structure before you agree to participate.",
            "There are no surprises. You should know what you're selling, what you're earning, and when you're getting paid before you start.",
        ],
    },
    {
        q: "Do I have to commit to a minimum sales volume?",
        a: [
            "Not necessarily.",
            "1099er is designed differently from traditional distributorship agreements that may require broad territory commitments or minimum purchase requirements.",
            "Some opportunities may have specific expectations, but our core model is built around targeted opportunities with representatives who already have relevant relationships.",
        ],
    },
    {
        q: "Do I need to carry inventory?",
        a: [
            "It depends on the product.",
            "Whenever possible, we want to remove unnecessary inventory and infrastructure requirements from the individual representative. Inventory, consignment, direct shipping, samples, and case-specific product will be addressed as part of each individual opportunity.",
        ],
    },
    {
        q: "What happens if another 1099er rep knows the same surgeon?",
        a: [
            "We'll address overlapping relationships before an opportunity moves forward.",
            "1099er isn't intended to create a free-for-all where multiple representatives compete for the same physician. When there is overlap, we'll evaluate the existing relationship, the status of the opportunity, and any prior activity before determining how to proceed.",
        ],
    },
    {
        q: "Is my information confidential?",
        a: [
            "Discretion is a core part of the 1099er model.",
            "We recognize that many people exploring opportunities may currently work for manufacturers, distributors, or other medical sales organizations. Your information is used to evaluate potential opportunities and should not be publicly displayed as a rep profile.",
        ],
    },
    {
        q: "What does it cost to join 1099er?",
        a: [
            "Nothing.",
            "There is no fee for a representative to submit their information, explore an opportunity, or have an initial conversation with 1099er.",
        ],
    },
    {
        q: "What happens after I submit my information?",
        a: [
            "We'll review your geography, experience, specialty, and general relationship profile against available opportunities.",
            "If we see a potential fit, we'll reach out for a confidential conversation.",
            "No lengthy application. No commitment. No need to provide your entire account list.",
            "The first step is simply determining whether there is an opportunity worth discussing.",
        ],
    },
];

const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
            "@type": "Answer",
            text: f.a.join(" "),
        },
    })),
};

export default function FaqPage() {
    return (
        <div data-testid="faq-page">
            <PageHeader
                eyebrow="FAQ"
                title="Frequently Asked Questions."
                description="Straight answers on how 1099er works — territories, contracting, commissions, confidentiality, and next steps."
                testId="faq-header"
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
            />
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20">
                    <ul data-testid="faq-list" className="divide-y divide-slate-200 border-y border-slate-200">
                        {FAQS.map((f, idx) => {
                            const id = `faq-${idx + 1}`;
                            return (
                                <li key={f.q}>
                                    <details className="group" data-testid={id}>
                                        <summary
                                            data-testid={`${id}-summary`}
                                            className="cursor-pointer list-none py-5 md:py-6 flex items-start justify-between gap-6 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
                                        >
                                            <h2 className="font-display text-lg md:text-xl font-semibold text-slate-900 leading-snug">
                                                {f.q}
                                            </h2>
                                            <span
                                                aria-hidden="true"
                                                className="mt-1 shrink-0 w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 text-slate-600 group-open:bg-slate-900 group-open:text-white group-open:border-slate-900 group-open:rotate-45 transition-all"
                                            >
                                                <Plus size={16} />
                                            </span>
                                        </summary>
                                        <div
                                            data-testid={`${id}-answer`}
                                            className="pb-6 md:pb-7 space-y-3 text-[15px] md:text-base text-slate-600 leading-relaxed"
                                        >
                                            {f.a.map((p, i) => (
                                                <p key={i}>{p}</p>
                                            ))}
                                        </div>
                                    </details>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </section>
        </div>
    );
}
